/**
 * CheckInEdit page - Edit today's check-in with form pre-fill.
 * Validates same-day edit restriction and handles errors.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetTodayCheckin, useUpdateCheckin } from '../services/api';
import CheckinForm from '../components/checkin/CheckinForm';
import { useToast, ToastContainer } from '../components/Toast';
import { CheckInInputs, SleepQuality, MoodOption } from '../types';

/**
 * Map legacy check-in inputs to the new 4-step structure.
 * Handles old int sleep_quality (1-5), old mood values, and missing new fields.
 */
function mapLegacyInputs(inputs: CheckInInputs): CheckInInputs {
  const mapped = { ...inputs };

  // Map legacy int sleep_quality (1-5) to string enum
  if (typeof mapped.sleep_quality === 'number') {
    const sleepMap: Record<number, SleepQuality> = {
      1: 'poor', 2: 'fair', 3: 'good', 4: 'great', 5: 'excellent'
    };
    mapped.sleep_quality = sleepMap[mapped.sleep_quality as number] || 'good';
  }

  // Map legacy mood values to new enum
  const moodLegacyMap: Record<string, MoodOption> = {
    'very_low': 'low',
    'good': 'positive',
    'great': 'positive',
  };
  if (mapped.mood && moodLegacyMap[mapped.mood]) {
    mapped.mood = moodLegacyMap[mapped.mood];
  }

  // Ensure body_symptoms defaults to ['none'] if empty/undefined
  if (!mapped.body_symptoms || mapped.body_symptoms.length === 0) {
    mapped.body_symptoms = ['none'];
  }

  return mapped;
}

const CheckInEdit: React.FC = () => {
  const navigate = useNavigate();
  const { data: todayCheckin, isLoading, isError } = useGetTodayCheckin();
  const updateCheckin = useUpdateCheckin();
  const [error, setError] = useState<string>('');
  const [initialInputs, setInitialInputs] = useState<CheckInInputs | null>(null);
  const { toasts, closeToast, success, error: showError } = useToast();

  // Pre-fill form with existing check-in data (handles legacy format)
  useEffect(() => {
    if (todayCheckin?.checkin) {
      setInitialInputs(mapLegacyInputs(todayCheckin.checkin.inputs));
    }
  }, [todayCheckin]);

  // Handle form submission
  const handleUpdateSubmit = async (inputs: CheckInInputs) => {
    if (!todayCheckin?.checkin) {
      setError('No check-in found for today');
      return;
    }

    try {
      setError('');
      await updateCheckin.mutateAsync({
        checkinId: todayCheckin.checkin._id,
        inputs
      });

      // Show success toast
      success('Check-in updated successfully! Your wellness guidance has been recalculated.');

      // Redirect to home page after short delay to show toast
      setTimeout(() => navigate('/'), 1000);
    } catch (err: any) {
      // Handle same-day edit validation error
      const detail = err.response?.data?.detail;
      let errorMessage: string;

      if (Array.isArray(detail)) {
        // Pydantic validation errors come as array of {type, loc, msg, input, url}
        errorMessage = detail.map((e: any) => e.msg || String(e)).join('; ');
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      } else {
        errorMessage = err.message || 'Failed to update check-in';
      }

      if (errorMessage.includes('previous days')) {
        const msg = 'Cannot edit check-ins from previous days. You can only edit today\'s check-in.';
        setError(msg);
        showError(msg);
      } else {
        setError(errorMessage);
        showError(errorMessage);
      }
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mb-4"></div>
        <p className="text-neutral-600 font-medium">Loading your check-in...</p>
      </div>
    );
  }

  // Error state - no check-in for today
  if (isError || !todayCheckin?.checkin) {
    return (
      <div className="max-w-3xl mx-auto animate-fade-in">
        <div className="card border-red-200 bg-red-50">
          <div className="flex items-start space-x-3">
            <span className="text-3xl">⚠️</span>
            <div>
              <h2 className="text-xl font-serif font-bold text-red-900 mb-2">
                No Check-In Found
              </h2>
              <p className="text-red-800 mb-4">
                You don't have a check-in for today. Please complete a check-in first before editing.
              </p>
              <button
                onClick={() => navigate('/')}
                className="px-5 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all duration-200"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main edit form
  return (
    <>
      <ToastContainer toasts={toasts} onClose={closeToast} />
      <div className="max-w-3xl mx-auto animate-fade-in">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-serif font-bold text-primary-800 mb-3">
          Edit Today's Check-In
        </h1>
        <p className="text-lg text-neutral-600 max-w-2xl leading-relaxed">
          Update your check-in information. Your wellness guidance will be recalculated automatically.
        </p>
      </div>

      {/* Same-day validation warning */}
      <div className="mb-6 p-4 bg-primary-50 border-l-4 border-primary-400 rounded-r-xl">
        <div className="flex items-start space-x-3">
          <span className="text-xl">ℹ️</span>
          <div>
            <p className="text-sm font-semibold text-primary-900 mb-1">
              Same-Day Edit Only
            </p>
            <p className="text-sm text-primary-800">
              You can only edit today's check-in. Once midnight passes, the check-in cannot be modified.
            </p>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-5 bg-red-50 border-l-4 border-red-400 rounded-r-xl animate-slide-down">
          <div className="flex items-start space-x-3">
            <span className="text-2xl">❌</span>
            <div>
              <p className="text-sm font-semibold text-red-900 mb-1">Update Failed</p>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Check-in form with pre-filled values */}
      {initialInputs && (
        <CheckinForm
          onSubmit={handleUpdateSubmit}
          isLoading={updateCheckin.isPending}
          initialValues={initialInputs}
          submitButtonText="Save Changes"
          onCancel={handleCancel}
        />
      )}

      {/* Current check-in timestamp info */}
      <div className="mt-6 p-4 bg-neutral-50 border border-neutral-200 rounded-xl">
        <div className="flex items-center justify-between text-sm text-neutral-600">
          <div>
            <span className="font-medium">Original check-in:</span>{' '}
            {new Date(todayCheckin.checkin.completed_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
            })}
          </div>
          {todayCheckin.checkin.updated_at !== todayCheckin.checkin.completed_at && (
            <div>
              <span className="font-medium">Last updated:</span>{' '}
              {new Date(todayCheckin.checkin.updated_at).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default CheckInEdit;
